"use client";

import { LoadingSpinner } from "@wtc/ui";
import { AttachmentSelectionList } from "./AttachmentSelectionList";
import { ClientFolderPicker } from "./ClientFolderPicker";
import { CreateDriveFolderForm } from "./CreateDriveFolderForm";
import { SaveAttachmentsResults } from "./SaveAttachmentsResults";
import { SelectedClientFolder } from "./SelectedClientFolder";
import type { useSaveAttachmentsToGoogleDriveState } from "./useSaveAttachmentsToGoogleDriveState";

interface SaveAttachmentsModalBodyProps {
  state: ReturnType<typeof useSaveAttachmentsToGoogleDriveState>;
}

export function SaveAttachmentsModalBody({ state }: SaveAttachmentsModalBodyProps) {
  if (state.uploadResults) {
    return <SaveAttachmentsResults uploadResults={state.uploadResults} />;
  }

  if (state.attachmentsError) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-sm text-red-800 dark:text-red-200">
          Error loading attachments:{" "}
          {state.attachmentsError instanceof Error
            ? state.attachmentsError.message
            : "Unknown error"}
        </p>
      </div>
    );
  }

  if (!state.hasAnyDriveAccess && !state.statusLoading) {
    return (
      <div className="p-4 bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-yellow-800 rounded-lg">
        <p className="text-sm text-yellow-800 dark:text-yellow-200">
          Google Drive is not configured. Please{" "}
          <a href="/crm/calendar/setup" className="font-semibold underline">
            set up Google Drive access
          </a>
          .
        </p>
      </div>
    );
  }

  if (state.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner className="h-5 w-5 mr-2" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    );
  }

  return (
    <>
      <AttachmentSelectionList
        attachments={state.attachments}
        onDeselectAll={state.deselectAllAttachments}
        onSelectAll={state.selectAllAttachments}
        onToggleAttachment={state.toggleAttachmentSelection}
        selectedAttachmentIds={state.selectedAttachmentIds}
      />

      <ClientFolderPicker
        clientFolders={state.clientFolders}
        isRefreshing={state.isRefreshing}
        listingLoading={state.listingLoading}
        onRefreshFolders={state.handleRefreshFolders}
        onSearchTermChange={state.setSearchTerm}
        onSelectClient={state.setSelectedClient}
        searchTerm={state.searchTerm}
        selectedClient={state.selectedClient}
      />

      <CreateDriveFolderForm
        hasAnyDriveAccess={state.hasAnyDriveAccess}
        isPending={state.createFolderMutation.isPending}
        newFolderName={state.newFolderName}
        onCancel={() => {
          state.setShowCreateFolderForm(false);
          state.setNewFolderName("");
        }}
        onNewFolderNameChange={state.setNewFolderName}
        onSubmit={state.handleCreateFolderSubmit}
        onToggleForm={() => state.setShowCreateFolderForm(!state.showCreateFolderForm)}
        showCreateFolderForm={state.showCreateFolderForm}
      />

      <SelectedClientFolder
        onClear={() => state.setSelectedClient(null)}
        selectedClient={state.selectedClient}
      />
    </>
  );
}
